import React, { useId } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

type ProgressBarTone = 'primary' | 'success' | 'warning' | 'danger' | 'neutral';
type TranslationNamespace = string | readonly string[];

export interface ProgressBarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  value: number;
  min?: number;
  max?: number;
  label?: string;
  labelKey?: string;
  showValue?: boolean;
  indeterminate?: boolean;
  size?: 'sm' | 'md' | 'lg';
  tone?: ProgressBarTone;
  trackClassName?: string;
  fillClassName?: string;
  valueFormatter?: (args: { value: number; min: number; max: number; percent: number }) => string;
  translationNs?: TranslationNamespace;
}

const toneClasses: Record<ProgressBarTone, string> = {
  primary: 'bg-emerald-500 [.theme-liquid-glass_&]:bg-violet-400/90',
  success: 'bg-emerald-500 [.theme-liquid-glass_&]:bg-emerald-400/90',
  warning: 'bg-amber-500 [.theme-liquid-glass_&]:bg-amber-300/90',
  danger: 'bg-rose-500 [.theme-liquid-glass_&]:bg-rose-400/90',
  neutral: 'bg-slate-500 [.theme-liquid-glass_&]:bg-white/60',
};

const sizeClasses = {
  sm: 'h-2',
  md: 'h-3',
  lg: 'h-4',
} as const;

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  min = 0,
  max = 100,
  label,
  labelKey,
  showValue = true,
  indeterminate = false,
  size = 'md',
  tone = 'primary',
  trackClassName,
  fillClassName,
  valueFormatter,
  translationNs,
  className,
  ...props
}) => {
  const { t } = useTranslation(translationNs);
  const labelId = useId();

  const safeMax = max <= min ? min + 1 : max;
  const clampedValue = Math.min(safeMax, Math.max(min, value));
  const percent = ((clampedValue - min) / (safeMax - min)) * 100;
  const resolvedLabel = labelKey ? t(labelKey) : label;
  const valueLabel = valueFormatter
    ? valueFormatter({ value: clampedValue, min, max: safeMax, percent })
    : `${Math.round(percent)}%`;

  return (
    <div className={clsx('w-full', className)} {...props}>
      {(resolvedLabel || showValue) && (
        <div className="mb-2 flex items-center justify-between gap-3">
          {resolvedLabel ? (
            <span id={labelId} className="text-sm font-semibold text-slate-700 [.theme-liquid-glass_&]:text-white/85">
              {resolvedLabel}
            </span>
          ) : <span />}
          {showValue && !indeterminate && (
            <span className="text-xs font-semibold text-slate-500 [.theme-liquid-glass_&]:text-white/60">
              {valueLabel}
            </span>
          )}
        </div>
      )}

      <div
        role="progressbar"
        aria-label={!resolvedLabel ? valueLabel : undefined}
        aria-labelledby={resolvedLabel ? labelId : undefined}
        aria-valuemin={indeterminate ? undefined : min}
        aria-valuemax={indeterminate ? undefined : safeMax}
        aria-valuenow={indeterminate ? undefined : clampedValue}
        className={clsx(
          'relative overflow-hidden rounded-full border-2 border-black bg-slate-100',
          '[.theme-liquid-glass_&]:border-white/20 [.theme-liquid-glass_&]:bg-white/10',
          sizeClasses[size],
          trackClassName
        )}
      >
        {indeterminate ? (
          <div
            data-slot="progressbar-fill"
            className={clsx(
              'absolute inset-y-0 left-0 w-2/5 rounded-full',
              'animate-pulse',
              toneClasses[tone],
              fillClassName
            )}
          />
        ) : (
          <div
            data-slot="progressbar-fill"
            className={clsx(
              'h-full rounded-full transition-[width] duration-300 ease-out',
              toneClasses[tone],
              fillClassName
            )}
            style={{ width: `${percent}%` }}
          />
        )}
      </div>
    </div>
  );
};
